import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, type ConflictChip, type LeadDetail, type LeadEdit } from "@/lib/api";
import { formatAcres, formatMoneyCents, formatPhone } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useToast } from "./Toast";
import { Badge, Button, Card, inputClass } from "./ui";

/**
 * The editable half of the lead card (§7.4, D-34).
 *
 * Everything here is a FACT ABOUT THE WORLD that SmarterContact also has an opinion on, which is what
 * separates it from the Stage card next to it: those are the CRM's own workflow state, and SC knows
 * nothing about them. The distinction matters because it is what decides the merge rule — an edit here
 * records a manual override and can end up in a conflict, and a stage change cannot.
 *
 * Read-only and deliberately absent: SC ids, ingest history, the message mirror. Editing a mirror of
 * someone else's system is a way to make two systems disagree without either one knowing.
 */
type FieldKey = keyof LeadEdit;

const FIELDS: {
  key: FieldKey;
  label: string;
  type?: "number" | "money";
  help?: string;
  /** Shown only for vacant land (v2.1 T6.3): a house's assessed value is not a land price. */
  vacantOnly?: boolean;
}[] = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "entity_name", label: "Entity" },
  { key: "phone_e164", label: "Phone", help: "+15551234567. A number another lead already has merges the two." },
  { key: "email", label: "Email" },
  { key: "mailing_raw", label: "Mailing address" },
  { key: "situs_raw", label: "Property address" },
  { key: "apn", label: "APN" },
  { key: "acres", label: "Acres", type: "number" },
  { key: "county", label: "County" },
  { key: "state", label: "State" },
  { key: "property_type", label: "Property type" },
  { key: "campaign", label: "Campaign" },
  {
    key: "tax_assessment_cents",
    label: "Assessed value (CAD)",
    type: "money",
    help: "In dollars. The anchor band is computed from it when there is no CAD land value.",
    vacantOnly: true,
  },
];

function currentValue(data: LeadDetail, key: FieldKey): string {
  const l = data.lead;
  const parcel = data.parcels[0];
  switch (key) {
    case "first_name":
      return l.first_name ?? "";
    case "last_name":
      return l.last_name ?? "";
    case "entity_name":
      return l.entity_name ?? "";
    case "phone_e164":
      return l.phone_e164 ?? "";
    case "email":
      return l.email ?? "";
    case "mailing_raw":
      return data.mailing_raw ?? "";
    case "situs_raw":
      return parcel?.situs_raw ?? "";
    case "apn":
      return parcel?.apn ?? "";
    case "acres":
      return l.parcel_acres === null || l.parcel_acres === undefined ? "" : String(l.parcel_acres);
    case "county":
      return l.parcel_county ?? "";
    case "state":
      return l.parcel_state ?? "";
    case "property_type":
      return l.property_type ?? "";
    case "campaign":
      return l.campaign ?? "";
    case "tax_assessment_cents":
      // Edited in dollars, stored in cents (CLAUDE.md: money is integer cents).
      return parcel?.tax_assessment_cents === null || parcel?.tax_assessment_cents === undefined
        ? ""
        : String(parcel.tax_assessment_cents / 100);
    default:
      return "";
  }
}

function display(key: FieldKey, value: string): string {
  if (!value) return "—";
  if (key === "phone_e164") return formatPhone(value);
  if (key === "acres") return formatAcres(value);
  if (key === "tax_assessment_cents") return formatMoneyCents(Math.round(Number(value) * 100));
  return value;
}

/** One field: a value until you click it, an input while you are in it. Enter saves, Esc cancels. */
function EditableField({
  data,
  field,
  owned,
  onSaved,
}: {
  data: LeadDetail;
  field: (typeof FIELDS)[number];
  owned: boolean;
  onSaved?: (changed: FieldKey) => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const original = currentValue(data, field.key);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(original);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(original);
  }, [original]);
  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  const save = useMutation({
    mutationFn: () => {
      const value = draft.trim();
      // An emptied field is a null, not an empty string: `""` in a text column reads as "we know it is
      // blank", and the D-7 merge rule treats null and empty differently on the way in.
      const stored =
        field.type === "money" && value !== ""
          ? Math.round(Number(value.replace(/[$,\s]/g, "")) * 100)
          : value;
      const patch = { [field.key]: value === "" ? null : stored } as LeadEdit;
      return api.editLeadFields(data.lead.id, patch);
    },
    onSuccess: (detail) => {
      setEditing(false);
      onSaved?.(field.key);
      // A phone merge can return a DIFFERENT lead — the survivor. Say so rather than letting the page
      // silently become another record.
      if (detail.lead.id !== data.lead.id) {
        toast.push("Two leads had this number, so they were merged into the one with more messages");
      } else {
        toast.push(`${field.label} saved`);
      }
      void qc.invalidateQueries();
    },
    onError: (e: Error) => {
      toast.push(e.message, "danger");
      setDraft(original);
    },
  });

  if (!editing) {
    return (
      <div className="flex gap-2" data-testid={`fact-${field.key}`}>
        <dt className="flex w-32 shrink-0 items-start gap-1 text-fg-muted">
          <span>{field.label}</span>
          {owned && (
            <span
              title="Edited by hand. Ingest leaves this alone unless SmarterContact changes it (D-34)."
              className="text-[10px] text-brand"
              data-testid={`owned-${field.key}`}
            >
              ✎
            </span>
          )}
        </dt>
        <dd className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={`edit-${field.key}`}
            title="Click to edit"
            className={cn(
              "w-full break-words rounded px-1 text-left hover:bg-surface-2",
              original ? "" : "text-fg-muted",
            )}
          >
            {display(field.key, original)}
          </button>
        </dd>
      </div>
    );
  }

  return (
    <div className="flex gap-2" data-testid={`fact-${field.key}`}>
      <dt className="w-32 shrink-0 pt-1 text-fg-muted">{field.label}</dt>
      <dd className="min-w-0 flex-1">
        <input
          ref={input}
          value={draft}
          type={field.type === "money" ? "text" : (field.type ?? "text")}
          inputMode={field.type === "money" ? "decimal" : undefined}
          step={field.type === "number" ? "0.001" : undefined}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (draft !== original) save.mutate();
              else setEditing(false);
            }
            if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              setDraft(original);
              setEditing(false);
            }
          }}
          onBlur={() => {
            if (!save.isPending) {
              setDraft(original);
              setEditing(false);
            }
          }}
          className={inputClass + " text-xs"}
          data-testid={`input-${field.key}`}
          aria-label={field.label}
        />
        {field.help && <p className="mt-0.5 text-[10px] text-fg-muted">{field.help}</p>}
      </dd>
    </div>
  );
}

/** One open conflict, with keep-mine / take-SC (D-34). */
function ConflictRow({ leadId, chip }: { leadId: string; chip: ConflictChip }) {
  const toast = useToast();
  const qc = useQueryClient();
  const resolve = useMutation({
    mutationFn: (choice: "keep_mine" | "take_sc") =>
      api.resolveConflict(leadId, { column_name: chip.column_name, choice }),
    onSuccess: (_d, choice) => {
      toast.push(choice === "take_sc" ? `${chip.label} now follows SmarterContact` : `${chip.label} kept`);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <li
      className="rounded border border-warn/40 bg-warn/10 px-2 py-1.5 text-xs"
      data-testid={`conflict-${chip.column_name}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="warn">Conflict</Badge>
        <span className="font-medium">{chip.label}</span>
      </div>
      <dl className="mt-1 space-y-0.5 text-[11px]">
        <div className="flex gap-2">
          <dt className="w-16 shrink-0 text-fg-muted">Yours</dt>
          <dd className="min-w-0 flex-1 break-words">{chip.manual_value ?? "—"}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-16 shrink-0 text-fg-muted">SC now</dt>
          <dd className="min-w-0 flex-1 break-words">{chip.incoming_value ?? "—"}</dd>
        </div>
      </dl>
      <div className="mt-1.5 flex gap-1">
        <Button
          size="sm"
          disabled={resolve.isPending}
          onClick={() => resolve.mutate("keep_mine")}
          data-testid={`keep-mine-${chip.column_name}`}
        >
          Keep mine
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={resolve.isPending}
          onClick={() => resolve.mutate("take_sc")}
          data-testid={`take-sc-${chip.column_name}`}
        >
          Take SC
        </Button>
      </div>
    </li>
  );
}

export function EditableFacts({
  data,
  registerFocus,
}: {
  data: LeadDetail;
  /** Lets the page's `E` key put the cursor in the first field (§7.4 keyboard flow). */
  registerFocus?: (focus: () => void) => void;
}) {
  const owned = new Set(data.overridden_fields ?? []);
  const conflicts = data.conflicts ?? [];
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerFocus?.(() => {
      const first = container.current?.querySelector<HTMLButtonElement>("[data-testid^='edit-']");
      first?.click();
    });
  }, [registerFocus]);

  return (
    <div ref={container} className="space-y-3">
      {conflicts.length > 0 && (
        <Card title={`Conflicts (${conflicts.length})`} data-testid="conflicts-card">
          <p className="mb-2 text-[11px] text-fg-muted">
            SmarterContact changed a field after you corrected it. Neither side wins automatically — that
            is the point.
          </p>
          <ul className="space-y-2">
            {conflicts.map((chip) => (
              <ConflictRow key={chip.column_name} leadId={data.lead.id} chip={chip} />
            ))}
          </ul>
        </Card>
      )}

      <Card title="Facts" data-testid="editable-facts">
        <dl className="space-y-1 text-xs">
          {FIELDS.filter(
            (field) =>
              !field.vacantOnly ||
              (data.parcels[0]?.property_type ?? data.lead.property_type ?? "vacant_land") ===
                "vacant_land",
          ).map((field) => (
            <EditableField
              key={field.key}
              data={data}
              field={field}
              owned={owned.has(field.key)}
            />
          ))}
        </dl>
        <p className="mt-2 text-[11px] text-fg-muted">
          Click a value to edit · Enter saves · Esc cancels. Every edit is a ledger event, and a field you
          edit is not overwritten by an import unless SmarterContact changes it afterwards.
        </p>
      </Card>
    </div>
  );
}
